import { useEffect, useState, useMemo } from 'react';
import ClassesController from 'src/Api/ClassesController';
import { Class } from 'src/Models/Class';
import { EntryPointData } from '../EntryPointData';
import ListView from './Components/ListView';
import GridView from './Components/GridView';
import FilterView from './Components/FilterView';
import { ClassesFilter } from 'src/Models/ClassesFilter';
import { CurrentFilter, CurrentPagination } from 'src/Models/CurrentFilter';
import Pagination from 'src/Components/Pagination';
import Select from 'react-select';
import { useHistory, useLocation } from 'react-router-dom';

export const data: EntryPointData = {
  rootId: 'root',
};

function App() {
  const [classes, setClasses] = useState<Class[]>([]);
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const [isListView, setListView] = useState(
    searchParams.get('view') === 'list' || searchParams.get('view') === null
      ? true
      : false
  );
  const [filters, setFilter] = useState<ClassesFilter>();
  const [paginationData, setPaginationData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [currentFilter, setCurrentFilter] = useState<CurrentFilter>({
    length: searchParams.get('length')?.split(',') || [],
    modality: searchParams.get('modality')?.split(',') || [],
    genre_id: searchParams.get('genre_id')?.split(',') || [],
    sort_by: searchParams.get('sort_by') || 'date',
    days: searchParams.get('days')?.split(',') || [],
    time_of_day: searchParams.get('time_of_day')?.split(',') || [],
    price: searchParams.get('price')?.split(',') || [],
    genre_category_id: searchParams.get('genre_category_id')?.split(',') || [],
    genre_feature_id: searchParams.get('genre_feature_id')?.split(',') || [],
  });

  const [currentPage, setCurrentPagination] = useState<CurrentPagination>({
    page: searchParams.get('page') || '1',
  });

  const toggleListView = (value: boolean) => setListView(value);
  const sortOptions = [
    { value: 'date', label: 'Start Date' },
    { value: 'length', label: 'Length' },
    { value: 'price', label: 'Price' },
  ];

  const root = document.getElementById('root');
  const mediaUrl = root?.getAttribute('data-static-content');

  const setDeepLinking = () => {
    Object.keys({ ...currentFilter }).forEach((key: string) => {
      let value: string =
        typeof currentFilter[key as keyof CurrentFilter] === 'string'
          ? (currentFilter[key as keyof CurrentFilter] as string)
          : (currentFilter[key as keyof CurrentFilter] as string[])?.join(',');
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    history.replace({ search: searchParams.toString() });
  };

  const setDeepLinkingPagination = (page: string) => {
    if (page) {
      searchParams.set('page', page);
    } else {
      searchParams.delete('page');
    }
    history.replace({ search: searchParams.toString() });
  };

  useEffect(() => {
    const query = async () => {
      const resultFilters = await ClassesController.getFilters();
      const dataFilter = resultFilters.success
        ? resultFilters.payload
        : undefined;
      setFilter(dataFilter);
    };
    query();
  }, []);

  useEffect(() => {
    const query = async () => {
      setLoading(true);
      setCurrentPagination({ page: currentPage.page });
      const result = await ClassesController.getClasses(currentFilter, {
        page: currentPage.page,
      });
      const data = result.success ? result.payload.data : [];
      const paginationData = result.success ? result.payload : [];

      setPaginationData(paginationData);

      const classes = await ClassesController.mapCourseInfo(data);
      setClasses(classes);
      setLoading(false);
      setDeepLinking();
    };
    query();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter]);

  const onPageChange = async (page?: string) => {
    setLoading(true);
    setCurrentPagination({
      ...currentPage,
      page,
    });
    const result = await ClassesController.getClasses(currentFilter, { page });
    const data = result.success ? result.payload.data : [];
    const paginationData = result.success ? result.payload : [];

    setPaginationData(paginationData);

    const classes = await ClassesController.mapCourseInfo(data);
    setClasses(classes);

    const resultFilters = await ClassesController.getFilters();
    const dataFilter = resultFilters.success
      ? resultFilters.payload
      : undefined;
    setFilter(dataFilter);
    setDeepLinkingPagination(page || '1');
    setLoading(false);
  };

  return (
    <>
      <header className='class_header'>
        <FilterView
          filters={filters}
          setCurrentFilter={setCurrentFilter}
          setCurrentPagination={setCurrentPagination}
          currentFilter={currentFilter}
          onAddFilter={(type: string, value: string | string[]) => {
            setCurrentFilter({
              ...currentFilter,
              [type]: value,
              genre_feature_id:
                type === 'genre_category_id'
                  ? []
                  : type === 'genre_feature_id'
                  ? (value as string[])
                  : currentFilter.genre_feature_id,
              genre_category_id:
                type === 'genre_id'
                  ? []
                  : type === 'genre_feature_id'
                  ? []
                  : type === 'genre_category_id'
                  ? (value as string[])
                  : currentFilter.genre_category_id,
              genre_id:
                type === 'genre_category_id'
                  ? []
                  : type === 'genre_id'
                  ? (value as string[])
                  : currentFilter.genre_id,
            });
            setCurrentPagination({ page: '1' });
            setDeepLinkingPagination('1');
          }}
        />
        <div className='view_type_container'>
          <div className='pagination_result'>
            {paginationData?.total > 0 && (
              <span>
                Showing {paginationData?.from} - {paginationData?.to} of{' '}
                {paginationData?.total} results
              </span>
            )}{' '}
          </div>
          <div className='view_type'>
            <div className='sort'>
              Sort by:
              <Select
                options={sortOptions}
                className='dropdown'
                onChange={(e) => {
                  setCurrentFilter({
                    ...currentFilter,
                    sort_by: e?.value as string,
                  });
                }}
                placeholder='Sort by'
                isSearchable={false}
                value={sortOptions.find(
                  (item) => item.value === currentFilter.sort_by
                )}
              />{' '}
            </div>
            <button
              className={isListView ? 'active' : ''}
              onClick={() => {
                searchParams.set('view', 'list');
                toggleListView(true);
                history.replace({ search: searchParams.toString() });
              }}
            >
              <img src={`${mediaUrl}List.svg`} alt='Icon List' />
            </button>
            <button
              className={!isListView ? 'active' : ''}
              onClick={() => {
                toggleListView(false);
                searchParams.set('view', 'grid');
                history.replace({ search: searchParams.toString() });
              }}
            >
              <img src={`${mediaUrl}Grid.svg`} alt='Grid List' />
            </button>
          </div>
        </div>
      </header>
      {classes.length > 0 && (
        <Pagination
          paginationData={paginationData}
          currentPage={currentPage}
          onPageChange={onPageChange}
          top={true}
          id='pagination-top-list'
          isCourses={false}
        />
      )}
      {classes.length === 0 && !loading && (
        <span className='no_results'>
          Check back soon. You’ll likely see options when we finish our next
          schedule.{''}
        </span>
      )}

      <div>
        {!isListView && <GridView classes={classes} loading={loading} />}
      </div>
      <div>
        {isListView && <ListView classes={classes} loading={loading} />}
      </div>

      {classes.length > 0 && (
        <Pagination
          paginationData={paginationData}
          currentPage={currentPage}
          onPageChange={onPageChange}
          top={false}
          id='pagination-bottom-list'
          isCourses={false}
        />
      )}
    </>
  );
}

export default App;
